import {
  UilTrash,
  UilPen,
  UilUpload,
  UilImport,
  UilPlus,
} from '@iconscout/react-unicons';
import {
  Button,
  ButtonGroup,
  Dialog,
  DialogContent,
  DialogActions,
  DialogTitle,
  Divider,
  IconButton,
  TextField,
  Box,
  Notification,
  Waiting,
  ConfirmDialog,
} from '@xbotvn/mui';
import {
  cloneDeep,
  get,
  includes,
  map,
  values,
} from 'lodash';
import React, {
  useMemo, useState,
} from 'react';
import {
  useDispatch, useSelector,
} from 'react-redux';

import { paginationApi } from '../../../../api';
import DropzoneModal from '../../../../components/DropzoneModal';
import Table from '../../../../components/Table';
import {
  CATEGORIES,
  DEFAULT_CATEGORIES,
} from '../../../../libs/config';
import {
  CATEGORY,
  FILE,
} from '../../../../redux/actions/constants';
import {
  importExcel,
} from '../../Helpers';

export default function Category() {
  const dispatch = useDispatch();

  const {
    categories,
    handling,
    activeUnit,
    categoryConfig,
  } = useSelector(({
    category,
    user,
    unit,
  }) => ({
    categories: category.data,
    handling: category.handling,
    activeUnit: user.activeUnit,
    categoryConfig: unit?.data?.[user?.activeUnit]?.config?.categories ?? CATEGORIES,
  }));

  const [editor, setEditor] = useState();
  const [showImport, setShowImport] = useState();
  const [removeConfirm, setRemoveConfirm] = useState(false);
  const [waiting, setWaiting] = useState(false);

  const columns = useMemo(() => [
    {
      Header: 'STT',
      style: {
        width: 100,
      },
      Cell: ({
        row,
      }) => row.index + 1,
    },
    {
      Header: 'Tên',
      accessor: 'name',
      style: {
        width: 350,
      },
    },
    {
      Header: 'Kí hiệu',
      accessor: 'symbol',
      style: {
        width: 350,
      },
    },
    {
      Header: () => (
        <Button
          startIcon={<UilPlus />}
          size="small"
          color="primary"
          variant="outlined"
          onClick={() => setEditor({
            unitId: activeUnit,
          })}
        >
          Thêm
        </Button>
      ),
      id: 'add',
      style: {
        width: 50,
      },
      Cell: (rowProps) => {
        const rowValue = get(rowProps, 'row.original', {});
        const isDefault = includes(map(DEFAULT_CATEGORIES, 'symbol'), rowValue?.symbol);
        return (
          <ButtonGroup>
            <IconButton
              disabled={isDefault}
              size="small"
              onClick={() => setEditor(cloneDeep(rowValue))}
              color="primary"
              sx={{ marginRight: '5px' }}
            >
              <UilPen />
            </IconButton>
            <Divider orientation="vertical" flexItem />
            <IconButton
              disabled={isDefault}
              color="error"
              size="small"
              sx={{ marginLeft: '5px' }}
              onClick={() => {
                setRemoveConfirm(rowValue._id);
              }}
            >
              <UilTrash />
            </IconButton>
          </ButtonGroup>
        );
      },
    },
  ], []);

  const renderEditor = () => (
    <Dialog
      open
      onClose={() => setEditor()}
      title={editor._id ? 'Sửa' : 'Thêm'}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle onClose={() => setEditor()}>{editor._id ? 'Sửa' : 'Thêm'}</DialogTitle>
      <DialogContent>
        <TextField
          required
          fullWidth
          style={{ marginBottom: '10px' }}
          label="Tên"
          value={editor.name || ''}
          onChange={(e) => setEditor((prev) => ({
            ...prev,
            name: e.target.value,
          }))}
        />
        <TextField
          required
          label="Ký hiệu"
          fullWidth
          value={editor.symbol || ''}
          onChange={(e) => setEditor((prev) => ({
            ...prev,
            symbol: e.target.value,
          }))}
        />
      </DialogContent>
      <DialogActions>
        <Button
          color="success"
          onClick={() => {
            if (!editor.name) {
              Notification.warn('Tên không được trống.');
              return;
            }
            if (!editor.symbol) {
              Notification.warn('Ký hiệu không được trống.');
              return;
            }
            if (includes(map(categories, 'symbol'), editor.symbol)) {
              Notification.warn('Ký hiệu đã tồn tại.');
              return;
            }
            dispatch({
              type: CATEGORY.handlers.update,
              data: {
                ...editor,
                symbol: editor.symbol.trim(),
              },
              onSuccess: () => setEditor(),
            });
          }}
        >
          Lưu
        </Button>
        <Button
          color="error"
          onClick={() => setEditor()}
        >
          Đóng
        </Button>
      </DialogActions>
    </Dialog>
  );

  return (
    <div>
      {editor ? renderEditor() : null}
      {(handling || waiting) && <Waiting />}
      {removeConfirm ? (
        <ConfirmDialog
          title="Bạn có chắc chắn muốn xóa?"
          color="error"
          confirmLabel="Xoá"
          cancelLabel="Huỷ"
          maxWidth="sm"
          fullWidth
          open
          onClose={() => setRemoveConfirm()}
          onConfirm={async () => {
            setWaiting(true);
            const { data } = await paginationApi.getCount('document', {
              condition: {
                unitId: activeUnit,
                categoryId: removeConfirm,
              },
            });
            const {
              count,
            } = data;
            if (count) {
              setWaiting(false);
              Notification.warn(`Có ${count} tài liệu đang sử dụng loại sách này.`);
            } else {
              dispatch({
                type: CATEGORY.handlers.remove,
                _id: removeConfirm,
              });
              setWaiting(false);
            }
            setRemoveConfirm();
          }}
        >
          <DialogContent />
        </ConfirmDialog>
      ) : null}
      {showImport && (
        <DropzoneModal
          onConfirm={(files) => {
            if (!files.length) return;
            importExcel({
              files,
              activeUnit,
              config: categoryConfig,
              onSuccess: (records) => {
                dispatch({
                  type: CATEGORY.handlers.import,
                  data: records.map((record) => ({ ...record, symbol: record.symbol.trim() })),
                });
                setShowImport();
              },
            });
          }}
          onClose={() => setShowImport()}
        />
      )}
      <Box marginLeft={1} padding={1}>
        <Button
          startIcon={<UilUpload />}
          size="small"
          color="success"
          sx={{
            marginRight: 1,
          }}
          onClick={() => setShowImport(true)}
        >
          Nhập từ Excel
        </Button>
        <Button
          startIcon={<UilImport />}
          size="small"
          color="primary"
          onClick={() => dispatch({
            type: FILE.handlers.download,
            data: {
              unitId: activeUnit,
              fileName: 'Mau_LoaiAnPham.xlsx',
            },
          })}
        >
          Tải file mẫu
        </Button>
      </Box>
      <Table columns={columns} data={values(categories)} alwaysShowPagination />
    </div>
  );
}
